import React, { FC, useState } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Button, Typography } from '@material-ui/core'
import { Link } from '@src/components/Link'
import { ApolloError } from 'apollo-boost'
import ModalSkeleton from '../ModalSkeleton'
import LoadingBox from '@src/components/LoadingBox'
import ListError from '@src/components/ListError'
import { isPast } from 'date-fns-next'
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl'
import messages from '@utils/messages'
import { useQuickOrder } from '@src/components/QuickOrder/utils/quickOrderContext'
import { MODALTYPE } from '../enum'
import {
  Quote,
  QuoteDetails,
  CartItemInput,
  CartItemType,
} from '@src/types/graphql-types'
import {
  QuoteDetailQuery,
  useQuoteDetailQuery,
  QuoteItemFragment,
} from '@src/queries/QuoteDetailsQuery.generated'
import { useConvertQuoteToOrderMutation } from '@src/mutations/ConvertQuoteToOrderMutation.generated'
import { sendCommonDetailClickEvent } from '@src/utils/analytics'
import {
  setAddToCartData,
  sendAddToCartAnalyticsEvent,
} from '@utils/analytics/cartAndCheckoutEvents'
import {
  DejaNewEventCategory as eventCategory,
  DejaNewEventAction as eventAction,
  AddToCartPagesEnum,
  DejaNewEventLabel as eventLabel,
} from '@utils/analytics/enums'
import { getAllCartRefetch, getCartRefetch } from '@utils/useCart'
import { sendAddQuoteItemsToCartEvent } from '@utils/tealiumAnalytics'
import { orderCenterRoutes, useRouter } from '@src/routes'
import { OriginalError, extractData } from '@utils/errorHandler'
import { useCurrentUser } from '@utils/useCurrentUser'
import {
  SnackbarMessages,
  useGlobalSnackbar,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import CircleLoader from '@src/components/CircleLoader'
import InfoIcon from '@material-ui/icons/Info'
import QuoteItemsTable, {
  ItemRow,
  QuoteItemsTableLoadingSkeleton,
} from './QuoteItemsTable'
import { getRedirctCartRoute } from '@utils/cartUtils'

const useStyles = makeStyles((theme: Theme) => ({
  alertInfo: {
    border: `1px solid ${theme.palette.primary.main}`,
    borderLeftWidth: '5px',
    borderRadius: '6px',
    marginBottom: theme.spacing(8),
    display: 'inline-flex',
    padding: theme.spacing(5, 6, 6, 4),
    '& > svg': {
      fill: theme.palette.primary.main,
      marginRight: theme.spacing(1),
    },
    '& > p': {
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  loadingBox: {
    marginRight: 0,
  },
  orderDetailsLink: { paddingLeft: theme.spacing(1) },
  expiredQuote: {
    color: theme.palette.error.main,
  },
}))

const QuotesModalTitle: FC<{
  data: QuoteDetailQuery | undefined
  error: ApolloError | undefined
  loading: boolean
  isQuoteExpired: boolean
}> = ({ data, error, loading, isQuoteExpired }) => {
  const classes = useStyles()
  const intl = useIntl()

  if (loading)
    return (
      <LoadingBox className={classes.loadingBox} height={24} width={0.75} />
    )
  if (error || !data || !data.quoteDetail)
    return <FormattedMessage {...messages.GENERIC_ERROR} />

  const { quote } = data.quoteDetail

  return (
    <>
      {intl.formatMessage(messages.QUOTE_MODAL_TITLE, {
        quoteNumber: quote.quoteId,
      })}
      {isQuoteExpired ? (
        <span className={classes.expiredQuote}>
          {intl.formatMessage(messages.EXPIRED)}
        </span>
      ) : (
        <>
          {intl.formatMessage(messages.VALID_UNTIL)}{' '}
          <FormattedDate
            value={new Date(quote.expirationDate)}
            year="numeric"
            month="short"
            day="numeric"
            timeZone="UTC"
          />
        </>
      )}
    </>
  )
}

const QuotesModalContent: FC<{
  data: QuoteDetailQuery | undefined
  error: ApolloError | undefined
  loading: boolean
  isQuoteExpired: boolean
}> = ({ data, error, loading, isQuoteExpired }) => {
  const classes = useStyles()
  const intl = useIntl()

  if (loading) return <QuoteItemsTableLoadingSkeleton />

  if (error || !data || !data.quoteDetail) return <ListError />

  return (
    <>
      <QuoteItemsTable>
        {data.quoteDetail.quote.quoteItems.map((item, index) => {
          return (
            <ItemRow
              key={index}
              index={index}
              name={(
                item.materialInfo?.name ||
                item.productDetails?.name ||
                ''
              ).toUpperCase()}
              number={item.materialNumber}
              remainingQuantity={item.remainingQty}
              quantity={item.qty}
              isQuoteExpired={isQuoteExpired}
            />
          )
        })}
      </QuoteItemsTable>
      {data.quoteDetail.quote.quoteId && (
        <div className={classes.orderDetailsLink}>
          <Link
            href={`${orderCenterRoutes.quoteDetail(
              data.quoteDetail.quote.quoteId
            )}?returnUrl=${encodeURIComponent(orderCenterRoutes.quotes())}`}
          >
            {/* jsx-a11y rules disabled because href is implicitly passed to anchor */}
            {}
            <a
              onClick={() => {
                const detailDate = `${intl.formatMessage(
                  messages.VALID_UNTIL
                )} ${intl.formatDate(
                  new Date(data.quoteDetail.quote.expirationDate),
                  {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'UTC',
                  }
                )}`
                const detailTitle = `${intl.formatMessage(
                  messages.QUOTE_MODAL_TITLE,
                  {
                    quoteNumber: data.quoteDetail.quote.quoteId,
                  }
                )} ${
                  isQuoteExpired
                    ? intl.formatMessage(messages.EXPIRED)
                    : detailDate
                }`
                sendCommonDetailClickEvent(
                  {
                    event: 'quick_order_widget_interaction',
                    action: 'visit quote details',
                    detail: detailTitle,
                    section: 'add to quick order',
                    component: 'modal',
                    elementType: 'link',
                    elementText: 'quote details',
                    linkUrl: `${orderCenterRoutes.quoteDetail(
                      data.quoteDetail.quote.quoteId || ''
                    )}?returnUrl=${encodeURIComponent(
                      orderCenterRoutes.quotes()
                    )}`,
                    coreEvent: 'no',
                  },
                  {
                    eventCategory: eventCategory.QuickOrderPage,
                    eventAction: eventAction.Quotes,
                    eventLabel: eventLabel.ModalQuoteDetails,
                    eventInteractionType: 0,
                  }
                )
              }}
            >
              <FormattedMessage {...messages.QUOTE_DETAILS} />
              {' >'}
            </a>
          </Link>
        </div>
      )}
    </>
  )
}

const mapQuoteItemsToQuoteItemInputs = (
  quoteItems: QuoteItemFragment[],
  quoteId: string | null | undefined,
  isRequestNewQuote = false
): CartItemInput[] => {
  return quoteItems?.map((item) => {
    const quantity = isRequestNewQuote ? item.originalQty : item.remainingQty
    return {
      materialNumber: item.materialNumber,
      quantity: quantity == null ? 1 : quantity,
      quoteNumber: isRequestNewQuote ? undefined : quoteId,
      quoteItemNumber: isRequestNewQuote ? undefined : item.lineNumber,
      promoCode: item.promoCode,
      productNote: item.productNote,
      type: item.type,
      configurationId: item.configurationId,
      yourReference: item.reference,
      brand: item.brandId,
      currency: item.currency,
      customerPrice:
        (item.type === CartItemType.Configurator && item.yourPrice) ||
        undefined,
    }
  })
}

const QuotesModalActions: FC<{
  isQuoteExpired: boolean
  data: QuoteDetailQuery | undefined
  loading: boolean
}> = ({ isQuoteExpired, data, loading }) => {
  const { pendingItems } = useQuickOrder()
  const itemsSelected = pendingItems.length > 0

  const quote = data?.quoteDetail.quote
  const noneRemaining = data?.quoteDetail.quote.quoteItems.every(
    (item) => item.remainingQty === 0
  )

  const router = useRouter()
  const { isCustomerServiceRep, isBlueErpIntegrationEnabled } = useCurrentUser()
  const refetchQueries = !isBlueErpIntegrationEnabled
    ? getCartRefetch()
    : getAllCartRefetch()
  const [convertQuoteToOrder] = useConvertQuoteToOrderMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  })
  const { formatMessage } = useIntl()
  const { setSnackbar } = useGlobalSnackbar()
  const [showLoader, setShowLoader] = useState(false)

  const submitConvertQuoteToOrder = async (
    items: CartItemInput[],
    quote: QuoteDetails | any
  ) => {
    try {
      setShowLoader(true)
      setAddToCartData(
        items,
        quote.quoteItems,
        AddToCartPagesEnum.QuickOrderQuotesModal
      )
      const { data } = await convertQuoteToOrder({
        variables: {
          input: {
            quoteIdentifier: quote.quoteId,
            items,
            isCsr: isCustomerServiceRep,
          },
        },
      })
      sendAddQuoteItemsToCartEvent(
        quote.quoteItems.map((quoteItem, i) => ({
          ...quoteItem,
          ...(items[i] && items[i].quantity && { qty: items[i].quantity }),
        }))
      )
      await setSnackbar(
        SnackbarMessages.ItemsAddedToCart,
        items.filter((item) => item.quantity > 0).length
      )
      sendAddToCartAnalyticsEvent()
      router.push(getRedirctCartRoute(data?.convertQuoteToOrder))
    } catch (err) {
      const { errors } = extractData(err as OriginalError)
      const code = errors.length > 0 ? errors[0].code : 'UNKNOWN_ERROR'
      const errorMessage = formatMessage({ id: code, defaultMessage: 'Error' })
      setSnackbar(SnackbarMessages.QuoteConvertError, errorMessage)
      setShowLoader(false)
    }
  }

  const requestNewQuote = async () => {
    if (quote) {
      await submitConvertQuoteToOrder(
        mapQuoteItemsToQuoteItemInputs(quote.quoteItems, quote.quoteId, true),
        quote
      )
    }
  }

  const addSelectedItemsToCart = async () => {
    if (quote) {
      await submitConvertQuoteToOrder(
        mapQuoteItemsToQuoteItemInputs(
          quote.quoteItems.filter((item, index) =>
            pendingItems.some((pendingItem) => pendingItem.id === index)
          ),
          quote.quoteId,
          false
        ),
        quote
      )
    }
  }

  const addAllItemsToCart = async () => {
    if (quote) {
      await submitConvertQuoteToOrder(
        mapQuoteItemsToQuoteItemInputs(quote.quoteItems, quote.quoteId, false),
        quote
      )
    }
  }

  if (loading) {
    return <></>
  }

  return (
    <>
      {showLoader && <CircleLoader />}
      {isQuoteExpired ? (
        <Button
          color="primary"
          onClick={() => requestNewQuote()}
          size="large"
          variant="contained"
        >
          <FormattedMessage
            id="REQUEST_NEW_QUOTE"
            defaultMessage="Request New Quote"
          />
        </Button>
      ) : (
        <>
          <Button
            color="primary"
            disabled={!itemsSelected}
            onClick={() => addSelectedItemsToCart()}
            size="large"
            variant="outlined"
          >
            <FormattedMessage {...messages.ADD_SELECTED_GO_TO_CART} />
          </Button>
          <Button
            color="primary"
            disabled={noneRemaining}
            onClick={() => addAllItemsToCart()}
            size="large"
            variant="contained"
          >
            <FormattedMessage {...messages.ADD_ALL_GO_TO_CART} />
          </Button>
        </>
      )}
    </>
  )
}

const QuotesModal: FC<{
  onClose: () => void
  open: boolean
  quote: Quote
}> = ({ onClose, open, quote }) => {
  const classes = useStyles()
  const quoteId = quote.quoteId ? quote.quoteId : ''
  const { loading, data, error } = useQuoteDetailQuery({
    variables: {
      quoteIdentifier: quoteId,
      isConfirmation: false,
    },
    fetchPolicy: 'cache-and-network',
  })

  const isQuoteExpired =
    data?.quoteDetail.quote.expirationDate &&
    isPast(data.quoteDetail.quote.expirationDate)
      ? true
      : false
  return (
    <ModalSkeleton
      type={MODALTYPE.QuotesModal}
      actions={
        <QuotesModalActions
          isQuoteExpired={isQuoteExpired}
          data={data}
          loading={loading}
        />
      }
      content={
        <>
          {!loading && !isQuoteExpired && (
            <div className={classes.alertInfo}>
              <InfoIcon />
              <Typography variant="body2">
                <FormattedMessage {...messages.QUOTES_MODAL_ALERT} />
              </Typography>
            </div>
          )}
          <QuotesModalContent
            data={data}
            error={error}
            loading={loading}
            isQuoteExpired={isQuoteExpired}
          />
        </>
      }
      onClose={() => onClose()}
      open={open}
      title={
        <QuotesModalTitle
          data={data}
          error={error}
          loading={loading}
          isQuoteExpired={isQuoteExpired}
        />
      }
    />
  )
}

export default QuotesModal
